/**
 * SEOHead — directe document.head manager (vervangt react-helmet-async).
 *
 * Reden: react-helmet-async v2.0.5 injecteert alleen <title> betrouwbaar.
 * <link rel="canonical">, <meta>, en JSON-LD <script> tags worden niet
 * geïnjecteerd in onze CRA + dev-server omgeving. useEffect-based
 * injection is voorspelbaar, sneller en heeft geen library-afhankelijkheid.
 *
 * Elke tag krijgt data-seohead="1" zodat we ze schoon kunnen verwijderen
 * bij unmount (zonder bestaande static tags uit index.html aan te raken).
 */
import { useEffect } from 'react';

const BASE_URL = 'https://www.imperiaai.nl';
const DEFAULT_OG_IMAGE =
  'https://static.prod-images.emergentagent.com/jobs/079d029a-43d6-48e1-8584-fd7e107daf53/images/58d2800a897fb6fc6bada372928156e5c6827933b3116d3856722bc213ba1013.png';

/** Schoonmaakhulp: verwijder álle tags die door SEOHead waren toegevoegd. */
function cleanup() {
  document.head.querySelectorAll('[data-seohead="1"]').forEach((el) => el.remove());
}

/** Voeg een tag toe met onze data-marker. */
function addTag(tagName, attrs, textContent) {
  const el = document.createElement(tagName);
  el.setAttribute('data-seohead', '1');
  Object.entries(attrs).forEach(([k, v]) => {
    if (v != null && v !== '') el.setAttribute(k, v);
  });
  if (textContent) el.textContent = textContent;
  document.head.appendChild(el);
}

/**
 * Verwijder ÉÉN bestaande tag uit index.html (zonder data-seohead marker)
 * — alleen als we hem gaan vervangen, anders blijft de statische tag staan.
 * Voorkomt dubbele meta description / og:title etc.
 */
function removeStaticTag(selector) {
  document.head.querySelectorAll(selector).forEach((el) => {
    if (!el.hasAttribute('data-seohead')) el.remove();
  });
}

export const SEOHead = ({
  title,
  description,
  keywords = '',
  canonical = '',
  ogImage = DEFAULT_OG_IMAGE,
  schema = null,
}) => {
  useEffect(() => {
    const fullTitle = title
      ? `${title} | IMPERIA AI`
      : 'IMPERIA AI — Online Marketing Bureau Zwolle';
    const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

    // 1. Title — direct via document.title (snelste + werkt altijd)
    const previousTitle = document.title;
    document.title = fullTitle;

    // 2. Remove conflicting STATIC tags die we gaan overschrijven, en eventuele
    //    achtergebleven tags van een vorige SEOHead-mount.
    cleanup();
    removeStaticTag('meta[name="description"]');
    removeStaticTag('meta[name="keywords"]');
    removeStaticTag('meta[property="og:title"]');
    removeStaticTag('meta[property="og:description"]');
    removeStaticTag('meta[property="og:url"]');
    removeStaticTag('meta[property="og:image"]');
    removeStaticTag('meta[property="og:type"]');
    removeStaticTag('meta[property="og:locale"]');
    removeStaticTag('meta[property="og:site_name"]');
    removeStaticTag('meta[name="twitter:card"]');
    removeStaticTag('meta[name="twitter:title"]');
    removeStaticTag('meta[name="twitter:description"]');
    removeStaticTag('meta[name="twitter:image"]');
    removeStaticTag('link[rel="canonical"]');

    // 3. Add fresh tags
    if (description) {
      addTag('meta', { name: 'description', content: description });
    }
    if (keywords) {
      addTag('meta', { name: 'keywords', content: keywords });
    }

    addTag('link', { rel: 'canonical', href: canonicalUrl });

    // Open Graph
    addTag('meta', { property: 'og:type', content: 'website' });
    addTag('meta', { property: 'og:site_name', content: 'IMPERIA AI' });
    addTag('meta', { property: 'og:locale', content: 'nl_NL' });
    addTag('meta', { property: 'og:title', content: fullTitle });
    if (description) {
      addTag('meta', { property: 'og:description', content: description });
    }
    addTag('meta', { property: 'og:url', content: canonicalUrl });
    addTag('meta', { property: 'og:image', content: ogImage });

    // Twitter
    addTag('meta', { name: 'twitter:card', content: 'summary_large_image' });
    addTag('meta', { name: 'twitter:title', content: fullTitle });
    if (description) {
      addTag('meta', { name: 'twitter:description', content: description });
    }
    addTag('meta', { name: 'twitter:image', content: ogImage });

    // JSON-LD schema (kan single object of array zijn) — render 1 script per item
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s) => {
        addTag('script', { type: 'application/ld+json' }, JSON.stringify(s));
      });
    }

    // Cleanup bij unmount
    return () => {
      cleanup();
      document.title = previousTitle;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, canonical, ogImage, JSON.stringify(schema)]);

  return null;
};

export default SEOHead;
